import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/store';
import { recordKeyResultProgress, fetchProgressHistory, clearProgressHistory } from '@/store/slices/okrsSlice';
import { GlassCard, GlassButton } from '@/design-system';
import { Input } from '@/components/ui';
import {
  TrendingUp,
  History,
  Loader2,
  AlertCircle,
  CheckCircle,
  ChevronRight,
} from 'lucide-react';
import type { KeyResult, KeyResultProgress } from '@/types';

interface KeyResultProgressModalProps {
  keyResult: KeyResult;
  onClose: () => void;
}

const KeyResultProgressModal: React.FC<KeyResultProgressModalProps> = ({
  keyResult,
  onClose,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { progressHistory, isLoadingProgress, error } = useSelector((state: RootState) => state.okrs);

  const [newValue, setNewValue] = useState<string>((keyResult.currentValue ?? keyResult.startValue).toString());
  const [note, setNote] = useState('');
  const [confidence, setConfidence] = useState<string>((keyResult.confidence ?? 50).toString());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    dispatch(fetchProgressHistory({ id: keyResult.id }));
    return () => {
      dispatch(clearProgressHistory());
    };
  }, [dispatch, keyResult.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSuccessMessage('');

    try {
      await dispatch(recordKeyResultProgress({
        id: keyResult.id,
        value: parseFloat(newValue),
        note: note || undefined,
      })).unwrap();

      setSuccessMessage('Progress updated successfully!');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Failed to record progress:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getProgressColor = (progress: number | null | undefined) => {
    if (progress === null || progress === undefined) return 'bg-gray-500';
    if (progress >= 75) return 'bg-green-500';
    if (progress >= 50) return 'bg-yellow-500';
    if (progress >= 25) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const calculateNewProgress = () => {
    const current = parseFloat(newValue) || 0;
    const start = keyResult.startValue;
    const target = keyResult.targetValue;

    if (target === start) return 0;
    const progress = ((current - start) / (target - start)) * 100;
    return Math.max(0, Math.min(100, progress));
  };

  const currentProgress = keyResult.progress ?? 0;
  const newProgress = calculateNewProgress();
  const progressDiff = newProgress - currentProgress;

  return (
    <div className="space-y-6">
      {/* Key Result Info */}
      <div className="bg-[var(--bg-tertiary)]/50 rounded-lg p-4">
        <p className="text-foreground font-medium mb-3">{keyResult.description}</p>
        <div className="flex items-center justify-between text-sm">
          <div className="text-muted-foreground">
            Current: <span className="text-foreground font-medium">{keyResult.currentValue ?? keyResult.startValue} {keyResult.unit}</span>
          </div>
          <div className="text-muted-foreground">
            Target: <span className="text-foreground font-medium">{keyResult.targetValue} {keyResult.unit}</span>
          </div>
        </div>
        <div className="mt-3">
          <div className="flex justify-between text-xs text-muted-foreground mb-1">
            <span>Progress</span>
            <span>{Math.round(currentProgress)}%</span>
          </div>
          <div className="w-full h-2 bg-[var(--bg-tertiary)]/50 rounded-full overflow-hidden">
            <div
              className={`h-full ${getProgressColor(currentProgress)} transition-all duration-300`}
              style={{ width: `${Math.min(currentProgress, 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Update Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-2">
            New Value
          </label>
          <div className="flex items-center gap-3">
            <Input
              type="number"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              min={keyResult.startValue}
              max={keyResult.targetValue}
              step="0.01"
              className="flex-1"
              required
            />
            <span className="text-muted-foreground">{keyResult.unit}</span>
          </div>
        </div>

        {/* Preview new progress */}
        <div className="bg-[var(--bg-tertiary)]/50 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">New Progress</span>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-foreground">{Math.round(newProgress)}%</span>
              {progressDiff !== 0 && (
                <span className={`text-sm ${progressDiff > 0 ? 'text-status-success' : 'text-status-error'}`}>
                  ({progressDiff > 0 ? '+' : ''}{Math.round(progressDiff)}%)
                </span>
              )}
            </div>
          </div>
          <div className="w-full h-2 bg-[var(--bg-tertiary)]/50 rounded-full overflow-hidden">
            <div
              className={`h-full ${getProgressColor(newProgress)} transition-all duration-300`}
              style={{ width: `${Math.min(newProgress, 100)}%` }}
            />
          </div>
        </div>

        {/* Note */}
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-2">
            Note (optional)
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note about this progress update..."
            className="w-full px-3 py-2 bg-[var(--bg-tertiary)]/50 border border-[var(--border-primary)] rounded-lg text-foreground placeholder-[var(--input-placeholder)] focus:outline-none focus:ring-2 focus:ring-blue-500/50 resize-none"
            rows={3}
          />
        </div>

        {/* Confidence slider */}
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-2">
            Confidence Level: <span className="text-foreground">{confidence}%</span>
          </label>
          <input
            type="range"
            value={confidence}
            onChange={(e) => setConfidence(e.target.value)}
            min="0"
            max="100"
            className="w-full h-2 bg-[var(--bg-tertiary)]/50 rounded-lg appearance-none cursor-pointer"
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>Low</span>
            <span>Medium</span>
            <span>High</span>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="flex items-center gap-2 p-3 bg-status-error-bg border border-status-error/30 rounded-lg">
            <AlertCircle className="w-5 h-5 text-status-error" />
            <span className="text-sm text-status-error">{error}</span>
          </div>
        )}

        {/* Success message */}
        {successMessage && (
          <div className="flex items-center gap-2 p-3 bg-status-success-bg border border-status-success/30 rounded-lg">
            <CheckCircle className="w-5 h-5 text-status-success" />
            <span className="text-sm text-status-success">{successMessage}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between items-center pt-4 border-t border-[var(--border-primary)]">
          <button
            type="button"
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <History className="w-4 h-4" />
            {showHistory ? 'Hide History' : 'Show History'}
            <ChevronRight className={`w-4 h-4 transition-transform ${showHistory ? 'rotate-90' : ''}`} />
          </button>

          <div className="flex gap-3">
            <GlassButton variant="ghost" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </GlassButton>
            <GlassButton variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Update Progress
                </>
              )}
            </GlassButton>
          </div>
        </div>
      </form>

      {/* Progress History */}
      {showHistory && (
        <div className="border-t border-[var(--border-primary)] pt-4">
          <h4 className="text-sm font-medium text-muted-foreground mb-3">Progress History</h4>
          {isLoadingProgress ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
            </div>
          ) : progressHistory.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No progress history yet</p>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {progressHistory.map((record) => (
                <div
                  key={record.id}
                  className="flex items-center justify-between p-2 bg-[var(--bg-tertiary)]/50 rounded-lg text-sm"
                >
                  <div>
                    <div className="text-foreground">{record.value} {keyResult.unit}</div>
                    {record.note && (
                      <div className="text-xs text-muted-foreground mt-0.5">{record.note}</div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-muted-foreground">{Math.round(record.progress ?? 0)}%</div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(record.recordedAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default KeyResultProgressModal;
